"use client";

import { useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { SubjectChoice } from "@/components/live/camera-subject-choice";
import type { CameraCapabilities, DeclaredCameras, DeclaredLens } from "@/lib/live/video/camera-capabilities";
import {
  DEFAULT_FRAMING,
  DEFAULT_SUBJECT,
  MIN_CAMERA_ZOOM,
  framingKey,
  framingTransform,
  framingView,
  framingViewStyle,
  isDefaultFraming,
  normaliseRotation,
  planFraming,
  seenSize,
  sameFraming,
  sourceFromOutput,
  splitRotation,
  type CameraFraming,
  type FrameSize,
  type FramingView,
} from "@/lib/live/video/camera-framing";
import {
  IDLE_DRAFT,
  arrivalDelayMs,
  draftFraming,
  draftReducer,
  shownAfter,
  type ShownFraming,
} from "@/lib/live/video/framing-preview";

/**
 * Le plus gros zoom qu'on propose.
 *
 * Le service en accepte jusqu'à cinq (`MAX_CAMERA_ZOOM`), mais au-delà de
 * quatre une caméra qui rogne n'a plus assez de pixels pour qu'on y lise des
 * doigts : d'une capture 720p, 4× laisse 320×180. Les bornes du service sont
 * ce qu'il **tolère** ; celle-ci est ce qu'on propose.
 */
const MAX_OFFERED_ZOOM = 4;

/**
 * De combien un cran de molette change le zoom, en proportion.
 *
 * Multiplicatif et non additif : un cran doit peser pareil à 1× et à 3×, sans
 * quoi le zoom se traîne en bas et s'emballe en haut.
 */
const WHEEL_ZOOM_PER_PIXEL = 0.0015;

/** Ce que le redressement propose de part et d'autre du quart de tour, en degrés. */
const STRAIGHTEN_RANGE = 20;

/**
 * Combien de temps un geste doit s'être arrêté avant que sa donnée parte, en
 * ms — pour un appareil appairé seulement, dont le cadrage se juge sur l'image
 * qui revient.
 *
 * Le service amortit ses écritures à 200 ms, et chaque écriture est une
 * métadonnée que toute la salle reçoit : envoyer plus souvent ne ferait pas
 * arriver le geste plus vite, et le ferait payer à chaque spectateur. L'image,
 * elle, n'attend pas : elle suit le doigt en CSS.
 */
const SEND_AFTER_MS = 200;

/**
 * Au bout de combien de temps, en ms, un cadrage validé que la séance n'a pas
 * republié est tenu pour perdu : l'aperçu revient alors à ce qui est publié,
 * plutôt que de montrer à jamais une image que la salle ne reçoit pas.
 *
 * Le trajet ordinaire est le service, son amortissement de 200 ms, et la
 * métadonnée qui revient : une demi-seconde au plus.
 */
const SENT_TIMEOUT_MS = 2000;

/**
 * Ce que le cadrage en cours demande à la vignette qui le montre.
 *
 * - `transform` : une transformation posée sur l'image reçue, le temps que les
 *   vrais pixels arrivent — le régime d'un appareil appairé ;
 * - `aspect` : le format que la vignette doit prendre pendant l'aperçu, quand
 *   celui-ci se dessine sur l'image entière — le régime de la webcam ; `null`
 *   pour garder celui de l'image reçue.
 */
export interface FramingPreview {
  readonly transform: string;
  readonly aspect: number | null;
}

/** Rien à poser : la vignette montre l'image reçue, telle quelle. */
export const NO_PREVIEW: FramingPreview = { transform: "", aspect: null };

/**
 * Les commandes de cadrage d'une caméra, sur son image mise en avant.
 *
 * # Ce que ces gestes font vraiment
 *
 * Rien à l'écran de qui les fait, ou presque. Ils publient un réglage que
 * **l'appareil qui filme** applique à sa propre capture, avant son encodeur :
 * les pixels écartés ne sortent pas de l'appareil. C'est la seule façon qu'un
 * recadrage masque vraiment quelque chose — posé à l'affichage, il laisserait
 * l'image entière chez chaque spectateur.
 *
 * # Pourquoi on glisse sur l'image plutôt que de pousser des curseurs
 *
 * Parce qu'un cadrage se juge à l'œil et se corrige au doigt. Deux curseurs
 * nommés disaient où ils en étaient, et ne disaient rien de ce qu'on allait
 * voir. Le déplacement et le zoom se font donc sur l'image ; il ne reste de
 * curseur que pour l'angle, qui n'a pas de geste naturel sur une image.
 *
 * # Deux caméras, deux aperçus
 *
 * La **webcam de ce poste** est là, entière : `source` est sa piste telle
 * qu'elle filme, avant tout cadrage. Le geste se dessine dessus, en local, et
 * rien ne part avant « Valider » — pas même un cadrage intermédiaire. Ce qu'on
 * découvre en déplaçant est là, puisque les pixels y sont ; et ce que la salle
 * reçoit reste l'ancien cadrage jusqu'à ce qu'on valide.
 *
 * D'un **appareil appairé** (`source` à `null`), il n'y a rien d'entier à
 * montrer : on ne reçoit de lui que ce qu'il a déjà rogné, et lui demander le
 * reste le ferait sortir de l'appareil. Le geste se dessine sur les pixels
 * reçus, la donnée part amortie pendant le geste, et c'est l'image qui revient
 * qui remplit ce qu'un déplacement a découvert. Un bouton « Valider » n'y
 * aurait pas de sens : sans les envois en cours de geste, on cadrerait à
 * l'aveugle au-delà du cadre reçu. Voir `docs/spec/video-engine.md`.
 */
export function CameraFramingControls({
  framing,
  participant,
  frame,
  readDeclared,
  source,
  mirrored = false,
  onPreview,
  onChange,
}: {
  /** Le cadrage que la séance publie pour cette caméra. */
  readonly framing: CameraFraming;
  /**
   * L'identité LiveKit de qui publie cette caméra : la clé sous laquelle elle
   * a déclaré ce qu'elle capte, et sous laquelle son cadrage se publie.
   */
  readonly participant: string;
  /** La définition de l'image reçue ; zéro tant qu'il n'en est pas arrivé. */
  readonly frame: FrameSize;
  /**
   * De quoi lire ce que les caméras ont déclaré capter.
   *
   * Passée en propriété et non prise au contexte : ce fichier porte le calcul
   * du geste, que les tests exercent sans navigateur, et tirer tout le
   * contexte de séance derrière lui le leur rendrait illisible.
   */
  readonly readDeclared?: () => Promise<DeclaredCameras>;
  /**
   * La caméra telle qu'elle filme, quand elle est sur ce poste : l'aperçu se
   * dessine dessus. `null` pour un appareil dont on ne reçoit que le cadré.
   */
  readonly source: MediaStream | null;
  /** L'image est montrée en miroir : le geste s'y fait à l'envers. */
  readonly mirrored?: boolean;
  /** Ce que la vignette doit poser pendant l'aperçu ; {@link NO_PREVIEW} à la fin. À garder stable. */
  readonly onPreview: (preview: FramingPreview) => void;
  /** Publie le cadrage ; vrai si le service l'a pris. */
  readonly onChange: (framing: CameraFraming) => Promise<boolean>;
}) {
  if (source !== null) {
    return (
      <LocalFraming framing={framing} source={source} mirrored={mirrored} onPreview={onPreview} onChange={onChange} />
    );
  }

  return (
    <RemoteFraming
      framing={framing}
      participant={participant}
      frame={frame}
      readDeclared={readDeclared}
      mirrored={mirrored}
      onPreview={onPreview}
      onChange={onChange}
    />
  );
}

/**
 * Le cadrage de la webcam de ce poste, sur son image entière.
 *
 * Le brouillon vit ici ({@link draftReducer}) et ne sort pas : la surface et
 * la barre le font bouger, l'aperçu le dessine sur la source, et seul
 * « Valider » l'envoie. Fermer la vignette pendant le geste l'abandonne — rien
 * n'était parti, la salle n'a rien vu, et la vignette revenue dans son coin
 * montre ce qui est publié.
 */
function LocalFraming({
  framing,
  source,
  mirrored,
  onPreview,
  onChange,
}: {
  readonly framing: CameraFraming;
  readonly source: MediaStream;
  readonly mirrored: boolean;
  readonly onPreview: (preview: FramingPreview) => void;
  readonly onChange: (framing: CameraFraming) => Promise<boolean>;
}) {
  const [draft, dispatch] = useReducer(draftReducer, IDLE_DRAFT);
  // Les dimensions de la caméra, dites par l'élément qui la montre : c'est lui
  // qui décode, et il les redit quand elle change de forme.
  const [size, setSize] = useState<FrameSize>({ width: 0, height: 0 });
  const shown = draftFraming(draft, framing);
  const view = framingView(size, shown);
  const aspect = view?.aspect ?? null;

  // La séance publie ce qu'on avait envoyé : l'attente est finie. Le reste du
  // temps, l'action ne change rien, et le réducteur rend le même état.
  useEffect(() => {
    dispatch({ type: "published", framing });
  }, [framing]);

  // Un envoi que la séance ne republie jamais — refusé en chemin, ou perdu —
  // ne doit pas laisser l'aperçu montrer une image que personne ne reçoit.
  useEffect(() => {
    if (draft.stage !== "sent") {
      return;
    }

    const timer = setTimeout(() => dispatch({ type: "discard" }), SENT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [draft]);

  // La vignette prend le format du cadrage qu'on dessine, quart de tour compris.
  useEffect(() => {
    onPreview({ transform: "", aspect });
  }, [aspect, onPreview]);

  useEffect(() => () => onPreview(NO_PREVIEW), [onPreview]);

  const gesture = (next: CameraFraming) => dispatch({ type: "gesture", framing: next });

  const validate = async () => {
    if (draft.stage !== "editing") {
      return;
    }

    // Revenu à ce qui est publié : rien à envoyer, et rien à attendre.
    if (sameFraming(draft.framing, framing)) {
      dispatch({ type: "discard" });

      return;
    }

    const sent = draft.framing;

    dispatch({ type: "validate" });

    if (!(await onChange(sent))) {
      dispatch({ type: "discard" });
    }
  };

  return (
    <div className="absolute inset-0 flex flex-col justify-end">
      <SourcePreview stream={source} view={view} mirrored={mirrored} onSize={setSize} />

      <FramingSurface source={size} framing={shown} mirrored={mirrored} onMove={gesture} onSettle={gesture} />

      <FramingBar
        framing={shown}
        onMove={gesture}
        onSettle={gesture}
        actions={
          draft.stage === "editing" ? (
            <>
              <TextButton onClick={() => dispatch({ type: "discard" })}>Annuler</TextButton>

              <TextButton accent onClick={() => void validate()}>
                Valider
              </TextButton>
            </>
          ) : undefined
        }
      />
    </div>
  );
}

/**
 * Le cadrage d'un appareil appairé, sur l'image qui revient de lui.
 *
 * Pendant le geste, l'image suit **ici**, en CSS, et la donnée part amortie :
 * un vrai debounce, où chaque mouvement repousse l'échéance, si bien qu'un
 * glissement continu n'écrit rien et qu'une seule transition a lieu, à la fin.
 * Faire l'aller-retour par le service pendant le geste ne pouvait pas suivre
 * un doigt.
 */
function RemoteFraming({
  framing,
  participant,
  frame,
  readDeclared,
  mirrored,
  onPreview,
  onChange,
}: {
  readonly framing: CameraFraming;
  /** L'identité LiveKit de qui publie : la clé de ce qu'elle a déclaré capter. */
  readonly participant: string;
  readonly readDeclared?: () => Promise<DeclaredCameras>;
  readonly frame: FrameSize;
  readonly mirrored: boolean;
  readonly onPreview: (preview: FramingPreview) => void;
  readonly onChange: (framing: CameraFraming) => Promise<boolean>;
}) {
  // Ce que l'image **montre**, qui n'est pas ce que l'état dit : la métadonnée
  // arrive avant les pixels.
  const { shown, source: guessed } = useShown(framing, frame);
  // Ce que la caméra a déclaré capter l'emporte sur ce qu'on devine depuis
  // l'image reçue : la déduction ne vaut plus dès que la découpe est
  // plafonnée, puisque toutes les découpes plus grandes que le plafond
  // publient la même définition. Voir `camera-capabilities.ts`.
  const declared = useDeclared(participant, frame, readDeclared);
  const source = declared === null ? guessed : { width: declared.width, height: declared.height };
  const [draft, setDraft] = useState(framing);
  /**
   * Un cadrage décidé ici que la séance n'a pas encore confirmé.
   *
   * C'est ce qui donne son sens à la transformation : tant qu'il dure, l'image
   * reçue montre l'ancien cadrage et le CSS fait la différence. Il tombe quand
   * la séance publie ce qu'on visait — la transformation redevient alors
   * l'identité d'elle-même.
   */
  const [pending, setPending] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(framing);

  // La séance a rattrapé ce qu'on visait : il n'y a plus de différence à
  // dessiner.
  if (pending && sameFraming(framing, draft)) {
    setPending(false);
  }

  // Et quand personne n'attend rien d'ici, c'est la métadonnée qui fait foi —
  // y compris quand le service a refusé le geste, ou qu'un autre onglet a
  // recadré.
  //
  // Comparés **par valeur** : la table est relue à chaque publication de
  // métadonnée — une position, une audience —, donc l'objet est neuf bien plus
  // souvent que le cadrage ne change, et comparer les identités reprendrait le
  // geste des mains à chaque seconde de récital.
  if (!pending && !sameFraming(draft, framing)) {
    setDraft(framing);
  }

  /**
   * La différence entre ce que l'image montre et ce qu'on vise, en CSS.
   *
   * **Une fonction pure des deux cadrages, et jamais un cumul.** C'était le
   * défaut du 2026-09-20 : la transformation tournait depuis le cadrage publié
   * pendant que des envois en cours de geste faisaient déjà tourner les
   * pixels, si bien que l'image tournait deux fois puis retombait. Calculée
   * ainsi, elle décroît d'elle-même à mesure que les pixels rattrapent, et vaut
   * l'identité dès qu'ils y sont.
   */
  const transform = framingTransform(shown, draft, source);

  useEffect(() => {
    onPreview({ transform, aspect: null });
  }, [transform, onPreview]);

  // Ce qui traîne s'arrête avec l'écran : un envoi en attente pour une caméra
  // qu'on ne regarde plus, une transformation posée sur une image qui s'en va.
  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }

      onPreview(NO_PREVIEW);
    },
    [onPreview],
  );

  /** Envoie ce qui attend, maintenant. */
  const flush = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    void onChange(latest.current);
  };

  const move = (next: CameraFraming) => {
    setDraft(next);
    setPending(true);
    latest.current = next;

    if (timer.current !== null) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(flush, SEND_AFTER_MS);
  };

  /** Au relâcher, ou sur un bouton : ce qui vaut part tout de suite. */
  const settle = (next: CameraFraming) => {
    setDraft(next);
    setPending(true);
    latest.current = next;
    flush();
  };

  return (
    <div className="absolute inset-0 flex flex-col justify-end">
      <FramingSurface source={source} framing={draft} mirrored={mirrored} onMove={move} onSettle={settle} />

      <FramingBar framing={draft} lenses={declared?.lenses} onMove={move} onSettle={settle} />
    </div>
  );
}

/**
 * L'image entière de la webcam, posée sur la vignette pendant qu'on cadre.
 *
 * Par-dessus la vidéo publiée, et non à sa place : changer la source d'un
 * élément vidéo coûte quelques images noires, et l'aperçu doit apparaître et
 * disparaître sans que rien ne clignote. Tant que la caméra n'a pas dit ses
 * dimensions, l'élément reste transparent et c'est la vidéo publiée qu'on voit
 * dessous — la même image, au cadrage près.
 *
 * Le miroir enveloppe **tout l'aperçu** : c'est une glace posée devant le
 * résultat, et une rotation dans le sens des aiguilles y apparaît dans l'autre
 * sens, exactement comme le fera l'image publiée vue en miroir.
 */
function SourcePreview({
  stream,
  view,
  mirrored,
  onSize,
}: {
  readonly stream: MediaStream;
  readonly view: FramingView | null;
  readonly mirrored: boolean;
  readonly onSize: (size: FrameSize) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;

    if (video === null) {
      return;
    }

    video.srcObject = stream;

    return () => {
      video.srcObject = null;
    };
  }, [stream]);

  // `resize` et non `loadedmetadata` seul : une webcam change de format quand
  // le navigateur rouvre sa capture, et l'aperçu doit suivre.
  useEffect(() => {
    const video = videoRef.current;

    if (video === null) {
      return;
    }

    const read = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        onSize({ width: video.videoWidth, height: video.videoHeight });
      }
    };

    read();
    video.addEventListener("loadedmetadata", read);
    video.addEventListener("resize", read);

    return () => {
      video.removeEventListener("loadedmetadata", read);
      video.removeEventListener("resize", read);
    };
  }, [stream, onSize]);

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ transform: mirrored ? "scaleX(-1)" : undefined }}
    >
      {/* `max-w-none` : la feuille de base borne toute vidéo à 100 % de large,
          et l'élément dépasse la vignette dès qu'on zoome. */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="absolute left-0 top-0 max-w-none"
        style={view === null ? { opacity: 0 } : { ...framingViewStyle(view), objectFit: "fill" }}
      />
    </div>
  );
}

/**
 * La barre des commandes qui ne sont pas des gestes : les quarts de tour, le
 * redressement, le sujet, le retour à l'image entière — et ce que le régime y
 * ajoute (`actions`).
 */
function FramingBar({
  framing,
  lenses,
  onMove,
  onSettle,
  actions,
}: {
  readonly framing: CameraFraming;
  /**
   * Les objectifs que l'appareil a déclarés. Le choix ne se propose qu'à
   * partir de deux : une caméra qui n'en a qu'un n'a rien à choisir, et un
   * menu d'une seule entrée serait une fausse promesse.
   */
  readonly lenses?: readonly DeclaredLens[];
  /** Pendant un curseur : la valeur passe, et peut encore bouger. */
  readonly onMove: (framing: CameraFraming) => void;
  /** Sur un bouton, ou au relâcher d'un curseur : la valeur vaut. */
  readonly onSettle: (framing: CameraFraming) => void;
  readonly actions?: ReactNode;
}) {
  const { quarters, tiltDeg } = splitRotation(framing.rotation);

  return (
    <div
      className="relative flex items-center gap-2 px-3 py-2.5"
      style={{
        background: "rgba(11, 11, 16, 0.72)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderTop: "1px solid rgba(255, 255, 255, 0.08)",
      }}
    >
      {lenses !== undefined && lenses.length >= 2 && (
        <LensChoice lenses={lenses} chosen={framing.lens} onChoose={(lens) => onSettle({ ...framing, lens })} />
      )}

      <IconButton
        label="Tourner vers la gauche"
        onClick={() => onSettle({ ...framing, rotation: normaliseRotation(framing.rotation - 90) })}
      >
        <RotateIcon back />
      </IconButton>

      <IconButton
        label="Tourner vers la droite"
        onClick={() => onSettle({ ...framing, rotation: normaliseRotation(framing.rotation + 90) })}
      >
        <RotateIcon />
      </IconButton>

      {/* Le seul curseur qui reste : un angle n'a pas de geste naturel sur
          une image, et il se règle au degré. Ce qu'il coûte en zoom pour
          remplir le cadre, l'appareil le calcule seul. */}
      <label className="flex flex-1 items-center gap-2">
        <span className="sr-only">Redresser</span>

        <input
          type="range"
          min={-STRAIGHTEN_RANGE}
          max={STRAIGHTEN_RANGE}
          step={1}
          value={tiltDeg}
          onChange={(event) =>
            onMove({ ...framing, rotation: normaliseRotation(quarters * 90 + Number(event.target.value)) })
          }
          onPointerUp={() => onSettle(framing)}
          onKeyUp={() => onSettle(framing)}
          className="h-1 flex-1 cursor-pointer rounded-full"
          style={{ accentColor: "var(--color-accent)" }}
        />

        <span className="w-9 shrink-0 text-right font-mono text-[11px] tabular-nums" style={{ color: "#ffffff" }}>
          {tiltDeg > 0 ? `+${tiltDeg}` : tiltDeg}°
        </span>
      </label>

      <span className="font-mono text-[11px] tabular-nums" style={{ color: "var(--color-text-secondary)" }}>
        {framing.zoom.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}×
      </span>

      <SubjectChoice
        subject={framing.subject ?? DEFAULT_SUBJECT}
        onChoose={(subject) => onSettle({ ...framing, subject })}
      />

      {/* Le sujet reste : ce n'est pas l'image, c'est ce qu'elle montre — et
          c'est lui qui place la caméra au replay. Rendre l'image entière en le
          ramenant à « mains » aurait envoyé un visage au pied du rouleau. */}
      {!isDefaultFraming({ ...framing, subject: null }) && (
        <TextButton
          onClick={() => onSettle({ ...DEFAULT_FRAMING, subject: framing.subject })}
          title="Rendre l'image entière"
        >
          Tout rendre
        </TextButton>
      )}

      {actions}
    </div>
  );
}

/**
 * La surface où l'on cadre : glisser déplace, la molette zoome.
 *
 * # Comment un glissement devient un cadrage
 *
 * Le plan du cadrage en cours dit de combien de pixels de la source le centre
 * peut encore bouger, et à quelle échelle l'image cadrée est montrée dans la
 * boîte. Le glissement s'y rapporte : de l'écran vers l'image cadrée (elles
 * n'ont pas la même taille), puis de l'image cadrée vers la source (elles
 * n'ont pas la même orientation — c'est là que l'angle entre en jeu). Le signe
 * est négatif parce qu'on déplace **l'image sous le cadre** : tirer vers la
 * droite montre ce qui était à gauche.
 *
 * Les dimensions de la source viennent de qui les connaît : la webcam les dit
 * elle-même, celles d'un téléphone se remontent depuis l'image reçue
 * ({@link sourceFromOutput}). Rien ne se fait tant qu'elles sont inconnues.
 */
function FramingSurface({
  source,
  framing,
  mirrored,
  onMove,
  onSettle,
}: {
  readonly source: FrameSize;
  readonly framing: CameraFraming;
  readonly mirrored: boolean;
  readonly onMove: (framing: CameraFraming) => void;
  readonly onSettle: (framing: CameraFraming) => void;
}) {
  const surface = useRef<HTMLDivElement | null>(null);
  const [grabbing, setGrabbing] = useState(false);
  // Ce que les écouteurs natifs doivent lire sans se rebrancher : ils vivent
  // une fois pour toutes, et le cadrage change à chaque image du geste.
  const state = useRef({ source, framing, mirrored, onMove, onSettle });

  // Après chaque rendu, sans liste de dépendances : les écouteurs natifs
  // ci-dessous sont posés une fois pour toutes, et doivent pourtant lire le
  // cadrage du moment — il change à chaque image du geste.
  useEffect(() => {
    state.current = { source, framing, mirrored, onMove, onSettle };
  });

  /*
    Des écouteurs **natifs**, et non les propriétés de React.

    React délègue les siens à la racine du document. Or la barre de commandes
    arrête le `pointerdown` avant qu'il n'y remonte — c'est ce qui empêche
    framer-motion d'emporter la vignette dès qu'on touche un curseur — si bien
    qu'un `onPointerDown` de React n'était jamais appelé ici. Posés sur la
    surface elle-même, ces écouteurs-ci s'exécutent avant cet arrêt, puisque
    l'événement remonte de l'intérieur.

    `passive: false` pour la molette : sans ça, le navigateur refuse
    `preventDefault` et la page défile sous le geste.
  */
  useEffect(() => {
    const element = surface.current;

    if (element === null) {
      return;
    }

    let start: { x: number; y: number; from: CameraFraming } | null = null;

    const down = (event: PointerEvent) => {
      const { source: size, framing: current } = state.current;

      if (size.width === 0) {
        return;
      }

      start = { x: event.clientX, y: event.clientY, from: current };
      setGrabbing(true);
      element.setPointerCapture(event.pointerId);
    };

    const move = (event: PointerEvent) => {
      const box = element.getBoundingClientRect();

      if (start === null || box.width === 0) {
        return;
      }

      const { source: size, mirrored: glass, onMove: report } = state.current;
      // Le miroir est une glace posée devant la caméra : à l'écran, ce qui part
      // à droite vient de la gauche de l'image.
      const dx = (event.clientX - start.x) * (glass ? -1 : 1);

      report(panned(start.from, size, box.width, dx, event.clientY - start.y));
    };

    const up = () => {
      if (start === null) {
        return;
      }

      start = null;
      setGrabbing(false);
      state.current.onSettle(state.current.framing);
    };

    const wheel = (event: WheelEvent) => {
      event.preventDefault();

      const { source: size, framing: current, mirrored: glass, onMove: report } = state.current;

      if (size.width === 0) {
        return;
      }

      const box = element.getBoundingClientRect();
      const zoom = clamp(
        current.zoom * Math.exp(-event.deltaY * WHEEL_ZOOM_PER_PIXEL),
        MIN_CAMERA_ZOOM,
        MAX_OFFERED_ZOOM,
      );

      report(
        zoomedAt(current, size, box, { x: event.clientX, y: event.clientY }, Math.round(zoom * 100) / 100, glass),
      );
    };

    element.addEventListener("pointerdown", down);
    element.addEventListener("pointermove", move);
    element.addEventListener("pointerup", up);
    element.addEventListener("pointercancel", up);
    element.addEventListener("wheel", wheel, { passive: false });

    return () => {
      element.removeEventListener("pointerdown", down);
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerup", up);
      element.removeEventListener("pointercancel", up);
      element.removeEventListener("wheel", wheel);
    };
  }, []);

  return (
    <div
      ref={surface}
      // `bottom-12` : la barre du bas garde ses gestes, et on ne cadre pas en
      // tirant sur un bouton.
      className="absolute inset-0 bottom-12 touch-none"
      style={{ cursor: grabbing ? "grabbing" : "grab" }}
    />
  );
}

/**
 * Le cadrage après un glissement de `dx`, `dy` pixels d'écran, l'image cadrée
 * `from` étant montrée sur `displayedWidth` pixels de large.
 *
 * L'échelle se lit sur le **cadrage montré**, pas sur l'image reçue : pendant
 * qu'un zoom est en route, la boîte montre déjà le cadre visé, plus serré que
 * ce qui arrive, et rapporter le geste à l'image reçue le faisait courir deux
 * fois trop vite.
 *
 * Exporté pour les tests : c'est la seule part du geste qui ait une réponse
 * juste ou fausse, et elle ne s'éprouve pas à la main.
 */
export function panned(
  from: CameraFraming,
  source: FrameSize,
  displayedWidth: number,
  dx: number,
  dy: number,
): CameraFraming {
  const plan = planFraming(source, from);

  if ((plan.marginX === 0 && plan.marginY === 0) || plan.output.width === 0 || displayedWidth === 0) {
    return from;
  }

  // De l'écran vers les pixels de la **source**, d'un coup : le champ vu n'a
  // plus la définition publiée depuis que la découpe est plafonnée, et prendre
  // celle-ci ferait courir le geste à côté.
  const scale = seenSize(plan).width / displayedWidth;
  const angle = (from.rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // De l'image cadrée vers la source : l'une est l'autre tournée de l'angle,
  // donc le déplacement se tourne en sens inverse.
  const sx = (dx * cos + dy * sin) * scale;
  const sy = (-dx * sin + dy * cos) * scale;

  return {
    ...from,
    x: plan.marginX === 0 ? from.x : clamp(from.x - sx / plan.marginX, -1, 1),
    y: plan.marginY === 0 ? from.y : clamp(from.y - sy / plan.marginY, -1, 1),
  };
}

/**
 * Le cadrage après un zoom **au point visé** : ce qui est sous le curseur y
 * reste.
 *
 * # Pourquoi ça ne va pas de soi
 *
 * Un zoom seul resserre autour du centre de l'image. Tout le monde attend
 * pourtant d'une molette qu'elle approche ce qu'on regarde, pas le milieu :
 * sans ça, on zoome, puis on déplace, puis on zoome encore pour rattraper.
 *
 * # Comment
 *
 * Le point de la **source** qui se trouve sous le curseur se lit dans le cadre
 * d'avant ; le centre d'après est celui qui le remet au même endroit du cadre
 * rétréci. Le repère tourne avec l'image, d'où la rotation inverse ; et le
 * miroir renverse l'axe horizontal, puisque ce qui est à droite de l'écran est
 * à gauche de l'image.
 *
 * Exportée pour les tests : c'est du calcul, et il ne s'éprouve pas à la main.
 */
export function zoomedAt(
  from: CameraFraming,
  source: FrameSize,
  box: { readonly left: number; readonly top: number; readonly width: number; readonly height: number },
  cursor: { readonly x: number; readonly y: number },
  zoom: number,
  mirrored = false,
): CameraFraming {
  const to = { ...from, zoom };

  if (source.width === 0 || box.width === 0 || box.height === 0) {
    return to;
  }

  const before = planFraming(source, from);
  const after = planFraming(source, to);

  if (before.draw === null || after.draw === null) {
    return to;
  }

  // Où se trouve le curseur dans le cadre montré, en fractions depuis son
  // centre.
  const fx = ((cursor.x - box.left) / box.width - 0.5) * (mirrored ? -1 : 1);
  const fy = (cursor.y - box.top) / box.height - 0.5;

  // De combien le cadre rétrécit, **en pixels de la source** : c'est ce dont
  // le centre doit se déplacer pour que le point visé ne bouge pas. Les champs
  // vus, donc, et non les définitions publiées — le plafond a séparé les deux.
  const seenBefore = seenSize(before);
  const seenAfter = seenSize(after);
  const dw = fx * (seenBefore.width - seenAfter.width);
  const dh = fy * (seenBefore.height - seenAfter.height);

  // Le cadre est tourné dans la source : son déplacement s'y exprime en
  // tournant en sens inverse.
  const angle = (from.rotation * Math.PI) / 180;
  const cx = before.draw.cx + dw * Math.cos(angle) + dh * Math.sin(angle);
  const cy = before.draw.cy - dw * Math.sin(angle) + dh * Math.cos(angle);

  return {
    ...to,
    x: after.marginX === 0 ? 0 : clamp((cx - source.width / 2) / after.marginX, -1, 1),
    y: after.marginY === 0 ? 0 : clamp((cy - source.height / 2) / after.marginY, -1, 1),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function IconButton({
  label,
  onClick,
  children,
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors hover:bg-white/[0.1] focus:outline-none focus-visible:ring-2"
      style={{ color: "#ffffff", cursor: "pointer", ["--tw-ring-color" as string]: "var(--color-accent)" }}
    >
      {children}
    </button>
  );
}

/** Un bouton en mots ; `accent` pour celui qu'on attend de l'utilisateur. */
function TextButton({
  onClick,
  title,
  accent = false,
  children,
}: {
  readonly onClick: () => void;
  readonly title?: string;
  readonly accent?: boolean;
  readonly children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="shrink-0 rounded-md px-2 py-1 text-[11px] transition-colors hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2"
      style={{
        color: accent ? "#ffffff" : "var(--color-text-secondary)",
        background: accent ? "var(--color-accent)" : undefined,
        fontWeight: accent ? 600 : undefined,
        cursor: "pointer",
        ["--tw-ring-color" as string]: "var(--color-accent)",
      }}
    >
      {children}
    </button>
  );
}

/** Une flèche qui fait le tour, dans un sens ou dans l'autre. */
function RotateIcon({ back = false }: { readonly back?: boolean }) {
  return (
    <svg
      aria-hidden
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      style={back ? { transform: "scaleX(-1)" } : undefined}
    >
      <path d="M11.5 5.5A5 5 0 1 0 12 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8.5 5.5h3.2V2.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Le cadrage que l'image reçue montre en ce moment, qui retarde sur celui que
 * la séance publie, et les dimensions de la caméra qui l'a filmée.
 *
 * # Pourquoi il retarde
 *
 * Parce qu'entre la métadonnée et les pixels il y a le trajet jusqu'à
 * l'appareil, sa toile, son encodeur, le retour, et la retenue qui cale
 * l'image sur les notes. Une demi-seconde, facilement. Prendre l'état publié
 * pour référence faisait réapparaître l'image d'origine le temps que les vrais
 * pixels arrivent, puis sauter.
 *
 * # Comment on sait qu'ils sont arrivés
 *
 * À la **définition de l'image** qui change ({@link shownAfter}) — et à un
 * délai ({@link arrivalDelayMs}) quand elle ne peut pas changer, ou ne change
 * pas. La règle vit dans `framing-preview.ts`, où elle s'éprouve sans
 * navigateur ; ce crochet ne fait que la brancher sur React.
 *
 * Le minuteur est armé sur la **valeur** du cadrage publié, et non sur son
 * objet : la table est relue à chaque publication de métadonnée, et un objet
 * neuf à chaque position aurait repoussé l'échéance sans fin.
 */
function useShown(framing: CameraFraming, frame: FrameSize): { shown: CameraFraming; source: FrameSize } {
  const [state, setState] = useState<ShownFraming>({ framing, frame });
  const next = shownAfter(state, framing, frame);

  // Pendant le rendu, comme React le veut d'un état qui se réajuste sur une
  // propriété. Sans boucle : `shownAfter` rend le même objet quand rien ne
  // change.
  if (next !== state) {
    setState(next);
  }

  const behind = !sameFraming(next.framing, framing);
  const delay = arrivalDelayMs(next, framing);
  const key = framingKey(framing);
  const published = useRef(framing);

  // Avant le minuteur ci-dessous, qui lit cette référence à l'échéance : les
  // effets s'exécutent dans l'ordre où ils sont écrits.
  useEffect(() => {
    published.current = framing;
  });

  useEffect(() => {
    if (!behind) {
      return;
    }

    const timer = setTimeout(
      () => setState((current) => ({ framing: published.current, frame: current.frame })),
      delay,
    );

    return () => clearTimeout(timer);
  }, [behind, delay, key]);

  return { shown: next.framing, source: sourceFromOutput(next.frame, next.framing) };
}

/**
 * Ce que cette caméra a déclaré capter, ou `null` si elle ne l'a pas dit.
 *
 * # Pourquoi une route et pas la métadonnée
 *
 * Parce que ces valeurs n'intéressent que qui cadre, et seulement pendant
 * qu'il cadre : les publier dans l'état de séance les enverrait à toute la
 * salle à chaque rotation de téléphone. Elles se lisent donc à la demande.
 *
 * # Quand on relit
 *
 * Au montage — on vient d'ouvrir le cadrage —, et **chaque fois que la
 * définition reçue change**. C'est le seul signal qu'on ait : un téléphone
 * qu'on tourne, une caméra qu'on rouvre, et ce qu'il avait déclaré ne vaut
 * plus. Il se redéclare de son côté au même instant, donc la relecture tombe
 * juste.
 *
 * Rien tant qu'on ne cadre pas : ce composant n'existe que sur une vignette
 * mise en avant.
 */
function useDeclared(
  participant: string,
  frame: FrameSize,
  read: (() => Promise<DeclaredCameras>) | undefined,
): CameraCapabilities | null {
  const [capture, setCapture] = useState<CameraCapabilities | null>(null);
  const size = `${frame.width}x${frame.height}`;

  useEffect(() => {
    if (read === undefined) {
      return;
    }

    let current = true;

    void read().then((cameras: DeclaredCameras) => {
      const declared = cameras[participant];

      if (!current) {
        return;
      }

      setCapture(declared ?? null);
    });

    return () => {
      current = false;
    };
  }, [read, participant, size]);

  return capture;
}

/**
 * Le choix de l'objectif, quand l'appareil en a plusieurs.
 *
 * # Ce que ce menu commande vraiment
 *
 * Il publie un `lens` dans le cadrage de la séance. C'est l'appareil qui le lit
 * et **rouvre sa caméra** : on ne commande pas un objectif à distance, on
 * demande, et il ouvre ce qu'il peut. Un objectif qui refuse de s'ouvrir laisse
 * la place à la caméra du côté filmé, et l'appareil le dit sur son écran.
 *
 * Rouvrir une caméra coupe l'image un instant. D'où `onSettle` et pas
 * `onMove` : ce n'est pas un geste qu'on ajuste, c'est un choix qu'on fait.
 *
 * # Un `select` et pas des boutons
 *
 * Parce que les noms viennent du navigateur de l'appareil et n'ont aucune
 * longueur promise — « back camera », « Caméra arrière triple (grand angle,
 * ...) », ou rien du tout. Des boutons côte à côte feraient déborder la barre
 * au premier téléphone bavard, là où un menu tient dans sa largeur et se
 * parcourt au doigt comme au clavier.
 *
 * « Automatique » n'est pas un objectif : c'est l'absence de demande, et donc
 * celui que l'appareil préfère pour le côté qu'il filme.
 */
function LensChoice({
  lenses,
  chosen,
  onChoose,
}: {
  readonly lenses: readonly DeclaredLens[];
  readonly chosen: string | null;
  readonly onChoose: (lens: string | null) => void;
}) {
  return (
    <label className="flex shrink-0 items-center gap-1.5">
      <span className="sr-only">Objectif</span>

      <LensIcon />

      <select
        value={chosen ?? ""}
        onChange={(event) => onChoose(event.target.value === "" ? null : event.target.value)}
        className="max-w-[140px] truncate rounded-md px-1.5 py-1 text-[11px] focus:outline-none focus-visible:ring-2"
        style={{
          color: "#ffffff",
          background: "rgba(255, 255, 255, 0.08)",
          border: "none",
          cursor: "pointer",
          ["--tw-ring-color" as string]: "var(--color-accent)",
        }}
      >
        <option value="">Automatique</option>

        {lenses.map((lens, index) => (
          // Le navigateur ne nomme ses caméras qu'une fois la permission
          // donnée, et iOS s'y prend lentement : un rang vaut mieux qu'une
          // ligne vide.
          <option key={lens.id} value={lens.id}>
            {lens.label === "" ? `Objectif ${index + 1}` : lens.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Un objectif, vu de face. */
function LensIcon() {
  return (
    <svg aria-hidden width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: "#ffffff" }}>
      <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
